import { cx } from '@emotion/css';
import {
  forwardRef,
  useCallback,
  KeyboardEvent,
  ChangeEvent,
  useEffect,
  useRef,
} from 'react';
import { useController, useFormContext } from 'react-hook-form';

import callAllFn from '@julofinance/web-helpers/dist/fn/callAllFn';
import mergeRefs from '@julofinance/web-helpers/dist/fn/mergeRefs';

import { removeNonNumber } from '@utils/number';
import { CheckCircle } from '@new-components/shapes';

import Button from '../button';
import FieldGroup from '../field-group';
import Input from '../input/InputBase';

import { BankAccountInputProps } from './types';
import { bankAccountInputCx } from './styles';

const BankAccountInput = forwardRef<HTMLDivElement, BankAccountInputProps>(
  (props, ref) => {
    const {
      name,
      label,
      helperText,
      className,
      inputProps,
      isVerified = false,
      isInvalid = false,
      isValidated = false,
      isVerifying,
      onVerify,
      disableVerify,
      onKeyDown,
      disabled,
      value,
      defaultValue,
      rules,
      required,
      inputRef = null,
      onChange,
      onBlur,
      ...resProps
    } = props;

    const { control, trigger } = useFormContext();
    const {
      fieldState: { error, invalid },
      field: {
        ref: controlRef,
        onChange: controllerOnChange,
        onBlur: controllerOnBlur,
        value: controllerValue,
      },
    } = useController({
      name,
      control,
      defaultValue,
      rules: {
        ...(required && { required: 'This field is required' }),
        ...(rules && rules),
      },
    });

    const prevIsValidated = useRef(isValidated);

    const handleOnKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
      if (
        e.key.length > 1 ||
        ((e.altKey || e.shiftKey || e.ctrlKey || e.metaKey) &&
          e.key.length <= 1)
      )
        return;
      if (/[^a-zA-Z0-9]/.test(e.key) || /^[a-zA-Z]$/.test(e.key))
        return e.preventDefault();
    }, []);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        e.target.setAttribute('data-bank-account-invalid', 'false');
        e.target.setAttribute('data-bank-account-verified', 'false');
        e.target.setAttribute('data-bank-account-validated', 'false');
        e.target.value = removeNonNumber(e.target.value);
      },
      [],
    );

    useEffect(() => {
      if (prevIsValidated.current !== isValidated) {
        trigger(name);
        prevIsValidated.current = isValidated;
      }
    }, [isValidated, name, trigger]);

    return (
      <FieldGroup
        invalid={invalid}
        error={error}
        helperText={helperText}
        className={cx(bankAccountInputCx, className)}
        ref={ref}
        name={name}
      >
        <div className='bank-account-wrapper'>
          <Input
            {...resProps}
            ref={ref}
            className='bank-account-input'
            inputProps={{
              ...inputProps,
              'data-bank-account-invalid': isInvalid,
              'data-bank-account-verified': isVerified,
              'data-bank-account-validated': isValidated,
              'data-bank-account-error-message': error?.message,
            }}
            inputRef={mergeRefs(controlRef, inputRef)}
            isInvalid={invalid}
            value={value ?? controllerValue}
            onChange={callAllFn(handleChange, onChange, controllerOnChange)}
            onBlur={callAllFn(onBlur, controllerOnBlur)}
            onKeyDown={callAllFn(handleOnKeyDown, onKeyDown)}
            disabled={disabled}
            label={label}
            name={name}
          />
          <Button
            className='bank-account-verify-btn'
            disabled={disableVerify || disabled || isVerified || isVerifying}
            variant={isVerified ? 'primary' : 'secondary'}
            onClick={onVerify}
          >
            {isVerified ? (
              <>
                <span>Terverifikasi</span>
                <CheckCircle width={20} height={20} className='check-icon' />
              </>
            ) : (
              'Verifikasi'
            )}
          </Button>
        </div>
      </FieldGroup>
    );
  },
);

export default BankAccountInput;
